'use client';

import { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';

interface Campaign {
  id: string;
  slug: string | null;
  content: string;
  send_count: number;
}

interface VsPair {
  slug_a: string;
  slug_b: string;
  total_sends: number;
  content_a: string;
  content_b: string;
}

function SlotCard({
  campaign,
  side,
  onClear,
}: {
  campaign: Campaign | null;
  side: 'A' | 'B';
  onClear: () => void;
}) {
  const isA = side === 'A';
  const borderColor = isA ? 'border-blue-500/60' : 'border-red-500/60';
  const labelColor = isA ? 'bg-blue-600' : 'bg-red-600';
  const emptyBorder = isA ? 'border-blue-500/30' : 'border-red-500/30';
  const emptyText = isA ? 'text-blue-400/60' : 'text-red-400/60';

  if (!campaign) {
    return (
      <div className={`flex-1 rounded-2xl border-2 border-dashed ${emptyBorder} p-6 flex flex-col items-center justify-center gap-3 min-h-[200px]`}>
        <span className={`text-4xl font-extrabold ${emptyText}`}>{side}</span>
        <p className="text-slate-500 text-sm text-center">Click a campaign below to add it here</p>
      </div>
    );
  }

  return (
    <div className={`flex-1 rounded-2xl border-2 ${borderColor} bg-[#fafaf7] p-5 flex flex-col gap-3 relative`}>
      <div className="flex items-center justify-between">
        <span className={`text-xs font-bold uppercase tracking-widest text-white px-3 py-1 rounded-full ${labelColor}`}>
          Side {side}
        </span>
        <button
          onClick={onClear}
          className="text-slate-400 hover:text-slate-600 text-lg leading-none transition-colors"
          aria-label={`Clear side ${side}`}
        >✕</button>
      </div>
      <div
        className="flex-1 bg-white rounded-xl border border-amber-200/60 p-3 text-slate-600 text-xs leading-relaxed relative overflow-hidden"
        style={{ backgroundImage: 'url("https://www.transparenttextures.com/patterns/cream-paper.png")' }}
      >
        <p className="font-bold mb-1 text-slate-700 text-[10px] uppercase tracking-wide">Dear Representative,</p>
        <p>{campaign.content}</p>
        <div className="absolute top-2 right-2 w-6 h-8 border border-amber-300/50 rounded-sm flex items-center justify-center text-[6px] text-slate-400 font-bold text-center leading-tight">
          POSTAGE
        </div>
      </div>
      <div className="flex items-center gap-1.5 text-xs font-semibold text-amber-700">
        <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
        {campaign.send_count.toLocaleString()} sent
      </div>
    </div>
  );
}

export default function VsPickerPage() {
  const router = useRouter();
  const [slotA, setSlotA] = useState<Campaign | null>(null);
  const [slotB, setSlotB] = useState<Campaign | null>(null);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Campaign[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [popularBattles, setPopularBattles] = useState<VsPair[]>([]);
  const nextSlotRef = useRef<'a' | 'b'>('a');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load trending on mount + pre-fill slots from ?a= ?b= params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const aParam = params.get('a');
    const bParam = params.get('b');

    fetch('/api/messages/trending')
      .then(r => r.json())
      .then(data => { setResults(Array.isArray(data) ? data : []); setListLoading(false); })
      .catch(() => setListLoading(false));

    fetch('/api/vs-trending?limit=5')
      .then(r => r.json())
      .then(data => { if (Array.isArray(data)) setPopularBattles(data); })
      .catch(() => {});

    if (aParam) {
      fetch(`/api/messages/${aParam}`)
        .then(r => r.ok ? r.json() : null)
        .then(data => { if (data && !data.error) { setSlotA(data); nextSlotRef.current = 'b'; } })
        .catch(() => {});
    }
    if (bParam) {
      fetch(`/api/messages/${bParam}`)
        .then(r => r.ok ? r.json() : null)
        .then(data => { if (data && !data.error) { setSlotB(data); } })
        .catch(() => {});
    }
  }, []);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setListLoading(true);
      try {
        const url = query.trim()
          ? `/api/messages/search?q=${encodeURIComponent(query)}`
          : '/api/messages/trending';
        const data = await fetch(url).then(r => r.json());
        setResults(Array.isArray(data) ? data : (data.messages ?? []));
      } catch {
        // keep existing results
      } finally {
        setListLoading(false);
      }
    }, 300);
  }, [query]);

  const assign = (campaign: Campaign) => {
    if (!slotA) {
      setSlotA(campaign);
      nextSlotRef.current = 'b';
    } else if (!slotB) {
      setSlotB(campaign);
      nextSlotRef.current = 'a';
    } else {
      if (nextSlotRef.current === 'a') {
        setSlotA(campaign);
        nextSlotRef.current = 'b';
      } else {
        setSlotB(campaign);
        nextSlotRef.current = 'a';
      }
    }
  };

  const compareHref = slotA && slotB
    ? `/vs/${slotA.slug || slotA.id}/${slotB.slug || slotB.id}`
    : null;

  return (
    <main className="flex-1 p-6 font-sans text-slate-200 animate-in fade-in duration-500">
      <div className="max-w-3xl mx-auto mt-12 sm:mt-20 pb-20 flex flex-col gap-8">

        {/* Header */}
        <div className="text-center animate-in fade-in slide-in-from-bottom-4 duration-500">
          <p className="text-xl font-bold uppercase tracking-widest text-amber-500 mb-3">Build a Comparison</p>
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-white mb-4">
            ⚔️ Pick your{' '}
            <span className="bg-gradient-to-r from-blue-400 to-red-400 bg-clip-text text-transparent">two sides.</span>
          </h1>
          <p className="text-lg text-slate-400 leading-relaxed max-w-xl mx-auto">
            Browse or search for two postcard campaigns below and assign each to a side. Then share the comparison to see which message gains more momentum.
          </p>
        </div>

        {/* Slots */}
        <div className="flex flex-col sm:flex-row gap-4 items-stretch">
          <SlotCard campaign={slotA} side="A" onClear={() => { setSlotA(null); nextSlotRef.current = 'a'; }} />

          <div className="flex sm:flex-col items-center justify-center gap-2 shrink-0">
            <div className="h-px sm:h-12 w-full sm:w-px bg-amber-500/20" />
            <span className="text-amber-500 font-extrabold text-xl shrink-0">VS</span>
            <div className="h-px sm:h-12 w-full sm:w-px bg-amber-500/20" />
          </div>

          <SlotCard campaign={slotB} side="B" onClear={() => { setSlotB(null); nextSlotRef.current = 'b'; }} />
        </div>

        {/* Compare button */}
        <div className="text-center">
          {compareHref ? (
            <button
              onClick={() => router.push(compareHref)}
              className="px-10 py-4 bg-gradient-to-r from-blue-700 to-red-700 hover:from-blue-600 hover:to-red-600 text-white font-bold rounded-xl text-lg transition-all shadow-lg active:scale-95"
            >
              Compare These Campaigns →
            </button>
          ) : (
            <button
              disabled
              className="px-10 py-4 bg-slate-700 text-slate-500 font-bold rounded-xl text-lg cursor-not-allowed"
            >
              {!slotA && !slotB ? 'Select two campaigns to compare' : 'Select one more campaign'}
            </button>
          )}
        </div>

        <p className="text-xs text-slate-500 text-center">
            Don&apos;t see your message?{' '}
            <Link href="/" className="text-amber-500/70 hover:text-amber-400 transition-colors">
              Start a campaign by simply sending the first postcard.
            </Link>
        </p>

        {/* Popular Battles */}
        {popularBattles.length > 0 && (
          <div className="flex flex-col gap-2">
            <p className="text-xs font-bold uppercase tracking-widest text-amber-500/70 mb-1">Popular Battles</p>
            {popularBattles.map(pair => {
              const excerptA = pair.content_a.length > 60 ? pair.content_a.slice(0, 59) + '…' : pair.content_a;
              const excerptB = pair.content_b.length > 60 ? pair.content_b.slice(0, 59) + '…' : pair.content_b;
              return (
                <Link
                  key={`${pair.slug_a}-${pair.slug_b}`}
                  href={`/vs/${pair.slug_a}/${pair.slug_b}`}
                  className="flex items-center gap-2 rounded-xl border border-amber-500/10 bg-[#0f172a]/60 hover:border-amber-500/30 hover:bg-[#111f35] px-4 py-3 transition-all group text-sm"
                >
                  <span className="text-blue-300 flex-1 truncate">{excerptA}</span>
                  <span className="text-amber-500 font-extrabold text-xs shrink-0">VS</span>
                  <span className="text-red-300 flex-1 truncate">{excerptB}</span>
                  <span className="text-slate-500 text-xs shrink-0 ml-1 group-hover:text-amber-400 transition-colors">→</span>
                </Link>
              );
            })}
          </div>
        )}

        {/* Search */}
        <div className="flex flex-col gap-4">
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-lg">🔍</span>
            <input
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search campaigns by message content..."
              className="w-full pl-11 pr-10 py-3.5 rounded-xl border border-amber-500/20 bg-[#0a1628] text-white placeholder-slate-500 focus:ring-2 focus:ring-amber-400/40 focus:border-amber-400/40 outline-none text-sm transition-all"
            />
            {query && (
              <button
                onClick={() => setQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white transition-colors text-lg leading-none"
                aria-label="Clear search"
              >✕</button>
            )}
          </div>

          <p className="text-xs text-slate-500 text-center">
            {query.trim() ? `Search results for "${query}"` : 'Trending campaigns this week — click to assign'}
          </p>
          

          {/* Results */}
          {listLoading ? (
            <div className="flex justify-center py-10">
              <div className="w-8 h-8 border-2 border-[#1e3050] border-t-amber-500 rounded-full animate-spin" />
            </div>
          ) : results.length === 0 ? (
            <div className="text-center py-10 text-slate-500 text-sm">
              {query.trim() ? 'No campaigns found. Try different keywords.' : 'No trending campaigns yet.'}
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {results.map(msg => {
                const isInA = slotA?.id === msg.id;
                const isInB = slotB?.id === msg.id;
                const inSlot = isInA ? 'A' : isInB ? 'B' : null;
                const cardExcerpt = msg.content.length > 120 ? msg.content.slice(0, 117) + '…' : msg.content;

                return (
                  <button
                    key={msg.id}
                    onClick={() => assign(msg)}
                    className={`w-full text-left rounded-xl border p-4 transition-all group ${
                      inSlot
                        ? inSlot === 'A'
                          ? 'border-blue-500/50 bg-blue-900/20'
                          : 'border-red-500/50 bg-red-900/20'
                        : 'border-amber-500/10 bg-[#0f172a]/60 hover:border-amber-500/30 hover:bg-[#111f35]'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <p className="text-slate-200 text-sm leading-relaxed flex-1">{cardExcerpt}</p>
                      <div className="shrink-0 flex flex-col items-end gap-1.5">
                        {inSlot ? (
                          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${inSlot === 'A' ? 'bg-blue-600 text-white' : 'bg-red-600 text-white'}`}>
                            Side {inSlot}
                          </span>
                        ) : (
                          <span className="text-xs text-amber-500 opacity-0 group-hover:opacity-100 transition-opacity font-semibold">
                            + Assign →
                          </span>
                        )}
                        <span className="text-xs text-slate-500">{msg.send_count.toLocaleString()} sent</span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>


        {/* Footer tip */}
        <p className="text-center text-xs text-slate-600">
          Don&apos;t see the campaign you want?{' '}
          <Link href="/" className="text-amber-500/70 hover:text-amber-400 transition-colors">
            Write a new postcard →
          </Link>
        </p>

      </div>
    </main>
  );
}
